import componentHashes from '@trustoo/components/component-hashes.json'

// Subject identity for design-system sign-off. Everything the designer can
// verify — one foundation token or one component — is addressed by
// (type, key, version), which is exactly the unique key on
// `design_system_verifications`. Keep the key builders here so the page and the
// modal can never disagree about what a row refers to.

export type VerificationSubjectType = 'foundation' | 'component'

export type VerificationStatus = 'pending' | 'approved' | 'changes_requested'

export interface VerificationSubject {
    type: VerificationSubjectType
    key: string
    // Always sent, even though foundations only have one generation today: a
    // future version gets its own row instead of overwriting this sign-off.
    version: string
    // Fingerprint of what is on screen right now. Compared against the stored
    // hash to spot an approval that predates a Figma change.
    hash: string
    label: string
}

// The token pipeline is unversioned so far, so every foundation row is v1. When
// token sets do get versioned this is the only place that changes.
export const FOUNDATION_VERSION = 'v1'

// Deliberately no app-wide COMPONENT_VERSION: each showcase declares the
// version it imports, so switching a showcase to /v3 moves its badge to v3 in
// the same edit rather than showing a v2 approval against new code.

const HASHES: Record<string, { hash?: string } | undefined> = componentHashes.components

/**
 * Non-cryptographic fingerprint of a token value. Only has to change when the
 * value changes, and has to be cheap: the Foundations page renders hundreds of
 * badges in one pass, so this stays synchronous rather than using crypto.subtle.
 */
export function fingerprint(value: unknown): string {
    const serialised = JSON.stringify(value) ?? 'null'
    let hash = 0x811c9dc5
    for (let i = 0; i < serialised.length; i++) {
        hash ^= serialised.charCodeAt(i)
        hash = Math.imul(hash, 0x01000193)
    }
    return (hash >>> 0).toString(16).padStart(8, '0')
}

/**
 * A foundation token, addressed by its path through the token tree:
 * `['colors', 'brand', '500']` or `['spacing', 'md']`.
 */
export function foundationSubject(path: string[], value: unknown): VerificationSubject {
    return {
        type: 'foundation',
        key: path.join('/'),
        version: FOUNDATION_VERSION,
        hash: fingerprint(value),
        label: path.join(' / '),
    }
}

/**
 * A design-system component. The fingerprint is the Figma node hash the sync
 * pipeline records, so an approval goes stale the moment the design moves.
 */
export function componentSubject(name: string, version: string): VerificationSubject {
    return {
        type: 'component',
        key: name,
        version,
        // A component with no entry in component-hashes.json has never been
        // through the sync pipeline; mark it so rather than silently sending an
        // empty hash that would look identical for every such component.
        hash: HASHES[name]?.hash ?? 'unsynced',
        label: `${name} (${version})`,
    }
}

export function subjectId(subject: {
    type: VerificationSubjectType
    key: string
    version: string
}): string {
    return `${subject.type}:${subject.key}:${subject.version}`
}
